// src/pages/billing/TransactionDetailPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useBillingTransaction, useI18n, usePayment } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatMoney(money: any): string {
  if (!money) return '--';
  const amount = (money.amount ?? 0) / Math.pow(10, money.decimal_places ?? 2);
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(dateStr); }
}

function txStatusVariant(status: string): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  switch (status) {
    case 'TRANSACTION_STATUS_COMPLETED': return 'success';
    case 'TRANSACTION_STATUS_PENDING': case 'TRANSACTION_STATUS_PROCESSING': return 'warning';
    case 'TRANSACTION_STATUS_FAILED': case 'TRANSACTION_STATUS_DISPUTED': return 'error';
    case 'TRANSACTION_STATUS_REFUNDED': case 'TRANSACTION_STATUS_PARTIALLY_REFUNDED': return 'primary';
    default: return 'secondary';
  }
}

function enumLabel(value: string, prefix: string): string {
  return (value || '').replace(prefix, '').replace(/_/g, ' ');
}

export const TransactionDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useBillingTransaction();
  // RefundTransaction moved from BillingService to PaymentService.RefundPayment.
  const paymentStore = usePayment();
  const { t } = useI18n();
  const [refundAmount, setRefundAmount] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  const item = store.currentItem as any;

  useEffect(() => {
    if (id) store.GetTransaction(id);
    return () => { store.clearCurrent(); };
  }, [id]);

  const handleRefund = async () => {
    const paymentId = item?.payment?.id;
    if (!id || !paymentId) return;
    setRefundLoading(true);
    try {
      await paymentStore.RefundPayment(paymentId, {
        amount: refundAmount ? { amount: Math.round(parseFloat(refundAmount) * 100), currency: item.amounts?.gross?.currency ?? 'CURRENCY_USD', decimal_places: 2 } : undefined,
      } as any);
      store.GetTransaction(id);
      setRefundAmount('');
    } catch {
      // Error handled by store
    } finally {
      setRefundLoading(false);
    }
  };

  if (store.isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }
  if (store.error) {
    return <><ErrorAlert error={store.error} /><Link to="/billing/transactions" className="mt-2 inline-block text-sm underline text-primary mt-2 inline-block text-sm underline text-primary">{t("common.backToList")}</Link></>;
  }
  if (!item) {
    return <Alert variant="warning" title={t('common.notFound')}><Link to="/billing/transactions" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></Alert>;
  }

  // Refunds go through PaymentService and require the linked payment id.
  const canRefund = item.status === 'TRANSACTION_STATUS_COMPLETED' && item.type === 'TRANSACTION_TYPE_CHARGE' && Boolean(item.payment?.id);

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/billing/transactions" className="text-muted-foreground hover:text-foreground">Transactions</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{String(id)}</span>
      </nav>

      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">Transaction</h1>
        <Badge variant={txStatusVariant(item.status)} size="sm">{enumLabel(item.status, 'TRANSACTION_STATUS_')}</Badge>
        <p className="text-sm text-muted-foreground mt-1">{enumLabel(item.type, 'TRANSACTION_TYPE_')} &middot; {String(item.description ?? '')}</p>
      </div>

      {/* Amounts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">Gross</p><p className="text-2xl font-bold text-foreground">{formatMoney(item.amounts?.gross)}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">Net</p><p className="text-2xl font-bold text-foreground">{formatMoney(item.amounts?.net)}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">Fee</p><p className="text-2xl font-bold text-foreground">{formatMoney(item.amounts?.fee)}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">Refunded</p><p className="text-2xl font-bold text-foreground">{formatMoney(item.refunded_amount)}</p></CardContent></Card>
      </div>

      {/* Details */}
      <Card>
        <CardHeader><CardTitle>Transaction Details</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Provider</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.provider ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Provider Transaction ID</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{String(item.provider_transaction_id ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Payment Method</dt>
              <dd className="text-sm text-foreground col-span-2">
                {item.payment_method ? `${item.payment_method.brand || ''} ****${item.payment_method.last_four || ''}` : '--'}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Idempotency Key</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{String(item.idempotency_key ?? '--')}</dd>
            </div>
            {item.failure_code && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Failure</dt>
                <dd className="text-sm text-destructive col-span-2">{item.failure_code}: {item.failure_message}</dd>
              </div>
            )}
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.created_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Processed</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.processed_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Settled</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.settled_at)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Refund action */}
      {canRefund && (
        <Card>
          <CardHeader><CardTitle>Refund Transaction</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Input label="Refund Amount (leave empty for full refund)" type="text" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} placeholder="0.00" />
              </div>
              <Button variant="danger" onClick={handleRefund} disabled={refundLoading}>
                {refundLoading ? 'Processing...' : 'Issue Refund'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
});

export default TransactionDetailPage;
